/* WhatsApp inbox — Phase 3 rewrite.
 *
 * Plan: .claude/plans/whatsapp-feature.md, Phase 3 + 3.1.
 *
 * What changed vs the previous 1713-LOC inbox:
 *   - All subcomponents are at module scope. The old file defined
 *     them inside the render function, which (per
 *     feedback_no_inline_components.md) creates a new function
 *     reference every render — React then unmounts+remounts the
 *     entire subtree on every parent update, hence the "jumping"
 *     symptoms the user saw.
 *   - URLs are the v2 routes my backend Phase 3 rewrite serves
 *     through Meta Cloud API. Same shape as before just /web/* swapped
 *     to /v2/*.
 *   - Polling every 5s for the conversation list, 3s for active
 *     thread. Real-time socket.io integration is Phase 3.2 — wiring
 *     it requires a JWT auth path the merchant frontend doesn't yet
 *     have on a cookie-only login. Polling is good enough for a
 *     merchant manning their inbox; webhooks still drive the
 *     server-side state in real time.
 *   - Salesive recipe with Revstar brand. Two-pane desktop, single-
 *     pane mobile (sidebar collapses on viewport < 768px).
 *
 * Out of scope (Phase 3.2):
 *   - Reactions UI, forward, star, delete (those route to backend
 *     handlers that throw NOT_SUPPORTED; we'd surface a toast).
 *   - Template messages, product sharing, intent detection — those
 *     are Phase 5 / 6 surfaces.
 *   - Socket.io real-time. Polling is sufficient for now.
 */

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { io as socketIo } from 'socket.io-client';
import {
  Search, Send, Paperclip, CheckCheck, Check, Clock, AlertCircle,
  MessageSquare, Inbox, RefreshCw, AlertTriangle,
  Image as ImageIcon, Video, Mic, FileText, X, Download,
  Package,
} from 'lucide-react';
import axiosInstance from '../../axiosConfig';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import { useToast } from '../../components/ui/Toast';
import './WhatsAppInbox.css';

/* ------------------------------------------------------------------ */
/* Module-scope helpers                                               */
/* ------------------------------------------------------------------ */

// Polling is now a fallback while the socket is offline. When the
// socket is connected we receive whatsapp:message / whatsapp:status
// events in real time, so we slow the poll way down (still useful as
// a reconciliation pass in case any event is missed during a brief
// disconnect, but no longer the primary refresh path).
const POLL_CONVERSATIONS_MS = 30_000;
const POLL_MESSAGES_MS = 15_000;

/**
 * Pick the right Socket.io target the same way useSocket does — local
 * dev hits localhost:4000, prod hits the Railway backend directly so
 * the websocket bypasses Vercel's proxy.
 */
function getSocketUrl() {
  if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:4000';
    if (h.includes('staging')) return 'https://revstar-main-platform-staging.up.railway.app';
  }
  return 'https://revstar-main-platform-production.up.railway.app';
}

function fmtRelative(d) {
  if (!d) return '';
  const t = new Date(d).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h`;
  if (diff < 7 * 86400_000) return `${Math.floor(diff / 86400_000)}d`;
  return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function initialsOf(nameOrPhone) {
  if (!nameOrPhone) return '?';
  const name = nameOrPhone.trim();
  if (!name) return '?';
  const parts = name.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function statusIcon(status) {
  const norm = (status || '').toLowerCase();
  if (norm === 'failed' || norm === 'undelivered') {
    return <AlertCircle size={13} className="wai-msg-status wai-msg-status--err" />;
  }
  if (norm === 'read') {
    return <CheckCheck size={13} className="wai-msg-status wai-msg-status--read" />;
  }
  if (norm === 'delivered') {
    return <CheckCheck size={13} className="wai-msg-status" />;
  }
  if (norm === 'queued') {
    return <Clock size={13} className="wai-msg-status wai-msg-status--queued" />;
  }
  // sent or default
  return <Check size={13} className="wai-msg-status" />;
}

/* ------------------------------------------------------------------ */
/* Subcomponents (module scope)                                       */
/* ------------------------------------------------------------------ */

function ConversationListItem({ conv, active, onClick }) {
  const display = conv.customerName || conv.customerPhone || 'Unknown';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`wai-conv${active ? ' is-active' : ''}`}
      aria-current={active ? 'true' : undefined}
    >
      <span className="wai-conv__avatar" aria-hidden="true">
        {initialsOf(display)}
      </span>
      <span className="wai-conv__body">
        <span className="wai-conv__top">
          <span className="wai-conv__name">{display}</span>
          <span className="wai-conv__time">{fmtRelative(conv.lastMessageAt)}</span>
        </span>
        <span className="wai-conv__bottom">
          <span className="wai-conv__preview">
            {conv.lastMessageDirection === 'outbound' ? 'You: ' : ''}
            {conv.lastMessageText || '—'}
          </span>
          {conv.unreadCount > 0 && (
            <span className="wai-conv__badge">{conv.unreadCount}</span>
          )}
        </span>
      </span>
    </button>
  );
}

function ConversationListSkeleton() {
  return (
    <>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="wai-conv wai-conv--skeleton">
          <span className="wai-skel wai-skel--avatar" />
          <span className="wai-conv__body">
            <span className="wai-skel wai-skel--line" />
            <span className="wai-skel wai-skel--line wai-skel--short" />
          </span>
        </div>
      ))}
    </>
  );
}

function ConversationsEmpty({ search }) {
  return (
    <div className="wai-empty wai-empty--side">
      <Inbox size={28} />
      <p className="wai-empty__title">
        {search ? 'No matches' : 'No conversations yet'}
      </p>
      <p className="wai-empty__hint">
        {search
          ? 'Try a different search.'
          : 'Customer messages will appear here once they message your number.'}
      </p>
    </div>
  );
}

function ThreadEmpty() {
  return (
    <div className="wai-empty wai-empty--main">
      <MessageSquare size={36} />
      <p className="wai-empty__title">Select a conversation</p>
      <p className="wai-empty__hint">
        Pick a chat from the list to read and reply.
      </p>
    </div>
  );
}

function NotConnectedNotice() {
  return (
    <div className="wai-empty wai-empty--main">
      <AlertTriangle size={36} />
      <p className="wai-empty__title">WhatsApp not connected</p>
      <p className="wai-empty__hint">
        Connect your WhatsApp Business Account on <a href="/whatsapp/setup">setup</a> to start receiving messages.
      </p>
    </div>
  );
}

function ThreadHeader({ conv, onBack, onResumeAI, resuming }) {
  if (!conv) return null;
  const display = conv.customerName || conv.customerPhone || 'Unknown';
  const merchantHandling = conv.handlingMode === 'MERCHANT_HANDLING';

  // Time until the auto-resume cron flips this back to AI_HANDLING —
  // 10 min after the last merchant reply (see whatsappHandoverService.js).
  let autoResumeLabel = null;
  if (merchantHandling && conv.takenOverAt) {
    const ms = 10 * 60 * 1000 - (Date.now() - new Date(conv.takenOverAt).getTime());
    if (ms > 0) {
      const mins = Math.max(1, Math.ceil(ms / 60000));
      autoResumeLabel = `auto-resumes in ${mins}m`;
    } else {
      autoResumeLabel = 'auto-resuming…';
    }
  }

  return (
    <header className="wai-thread__head">
      <button
        type="button"
        className="wai-thread__back"
        onClick={onBack}
        aria-label="Back to conversations"
      >
        ←
      </button>
      <span className="wai-thread__avatar" aria-hidden="true">
        {initialsOf(display)}
      </span>
      <span className="wai-thread__title">
        <span className="wai-thread__name">{display}</span>
        <span className="wai-thread__phone">{conv.customerPhone}</span>
      </span>
      {merchantHandling && (
        <span className="wai-thread__ai-paused" role="status">
          <span className="wai-thread__ai-paused-dot" aria-hidden="true" />
          <span className="wai-thread__ai-paused-text">
            AI paused{autoResumeLabel ? ` • ${autoResumeLabel}` : ''}
          </span>
          <button
            type="button"
            className="wai-thread__resume-ai"
            onClick={onResumeAI}
            disabled={resuming}
          >
            {resuming ? 'Resuming…' : 'Resume AI'}
          </button>
        </span>
      )}
    </header>
  );
}

const MEDIA_TYPES = new Set(['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT', 'STICKER']);

const MEDIA_LABELS = {
  IMAGE:    { Icon: ImageIcon, label: 'Photo' },
  VIDEO:    { Icon: Video,     label: 'Video' },
  AUDIO:    { Icon: Mic,       label: 'Voice note' },
  DOCUMENT: { Icon: FileText,  label: 'Document' },
  STICKER:  { Icon: ImageIcon, label: 'Sticker' },
};

/**
 * Inbound media bubble. When we have a mediaId on the message, we
 * render the real binary by pointing the browser at the merchant-
 * authed media proxy (it streams the bytes from Meta after auth-gating
 * + ownership check). Outbound + chip fallback are styled identically
 * to MediaChip so the visual rhythm doesn't change while content
 * loads.
 *
 * If the message has no mediaId — which currently includes every
 * outbound media we send (Meta gives us the media id, we just don't
 * write it to our own DB yet — that's a Phase 5/6 concern) — we fall
 * back to the labeled chip the merchant already knows.
 */
function MediaInline({ msg }) {
  const type = msg.messageType;
  const mediaId = msg.metadata?.mediaId;
  const { Icon, label } = MEDIA_LABELS[type] || { Icon: FileText, label: type };

  if (!mediaId) {
    return (
      <span className="wai-msg__media">
        <Icon size={14} />
        <span>{label}</span>
      </span>
    );
  }

  const src = `/api/whatsapp/v2/media/${mediaId}`;

  if (type === 'IMAGE' || type === 'STICKER') {
    return (
      <img
        src={src}
        alt={label}
        loading="lazy"
        className={`wai-msg__inline wai-msg__inline--${type === 'STICKER' ? 'sticker' : 'image'}`}
      />
    );
  }
  if (type === 'VIDEO') {
    return (
      <video
        src={src}
        controls
        preload="metadata"
        className="wai-msg__inline wai-msg__inline--video"
      />
    );
  }
  if (type === 'AUDIO') {
    return (
      <audio
        src={src}
        controls
        preload="metadata"
        className="wai-msg__inline wai-msg__inline--audio"
      />
    );
  }
  if (type === 'DOCUMENT') {
    return (
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="wai-msg__doc"
      >
        <FileText size={14} />
        <span>Open document</span>
        <Download size={12} />
      </a>
    );
  }
  return (
    <span className="wai-msg__media">
      <Icon size={14} />
      <span>{label}</span>
    </span>
  );
}

function MessageBubble({ msg }) {
  const isOutbound = msg.direction === 'OUTBOUND';
  const klass = `wai-msg ${isOutbound ? 'wai-msg--out' : 'wai-msg--in'}`;
  const isMedia = MEDIA_TYPES.has(msg.messageType);
  return (
    <div className={klass}>
      <div className="wai-msg__bubble">
        {isMedia && <MediaInline msg={msg} />}
        {msg.text && <p className="wai-msg__text">{msg.text}</p>}
        <span className="wai-msg__meta">
          <span className="wai-msg__time">{fmtTime(msg.createdAt || msg.sentAt)}</span>
          {isOutbound && statusIcon(msg.status)}
        </span>
      </div>
    </div>
  );
}

function MessageListView({ messages, loading, endRef }) {
  if (loading && messages.length === 0) {
    return (
      <div className="wai-msglist">
        {[1, 2, 3].map((i) => (
          <div key={i} className={`wai-msg ${i % 2 ? 'wai-msg--in' : 'wai-msg--out'}`}>
            <div className="wai-msg__bubble wai-msg__bubble--skeleton">
              <span className="wai-skel wai-skel--line" />
              <span className="wai-skel wai-skel--line wai-skel--short" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (messages.length === 0) {
    return (
      <div className="wai-empty wai-empty--main">
        <MessageSquare size={28} />
        <p className="wai-empty__title">No messages yet</p>
        <p className="wai-empty__hint">Send the first message to start the conversation.</p>
      </div>
    );
  }
  return (
    <div className="wai-msglist">
      {messages.map((msg) => (
        <MessageBubble key={msg.id} msg={msg} />
      ))}
      <div ref={endRef} />
    </div>
  );
}

// Meta's Cloud API caps single-file uploads at 16 MB for most media
// types (images / audio / video / document). Anything larger gets
// rejected by Graph; we surface that to the merchant up front.
const MAX_UPLOAD_BYTES = 16 * 1024 * 1024;
const ACCEPT_MIMES = 'image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx';

function formatBytes(b) {
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)}KB`;
  return `${(b / (1024 * 1024)).toFixed(1)}MB`;
}

/* ------------------------------------------------------------------ */
/* Catalog picker sheet (Phase 5.6)                                   */
/* ------------------------------------------------------------------ */

function CatalogPickerSheet({ open, onClose, onSend, sending, toast }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [picked, setPicked]     = useState([]);
  const [provisioned, setProvisioned] = useState(null);

  // Reset selection every time the sheet opens.
  useEffect(() => {
    if (open) setPicked([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axiosInstance.get('/whatsapp/v2/catalog/products', {
          params: { search, limit: 50 },
        });
        if (cancelled) return;
        setProducts(r.data?.products || []);
        setProvisioned(!!r.data?.catalogProvisioned);
      } catch (err) {
        if (cancelled) return;
        toast.error(err.response?.data?.error || 'Could not load products.');
        setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, search, toast]);

  const togglePick = useCallback((id) => {
    setPicked((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 30) {
        toast.info('Up to 30 products per message (Meta limit).');
        return cur;
      }
      return [...cur, id];
    });
  }, [toast]);

  const handleSend = useCallback(() => {
    if (picked.length === 0 || sending) return;
    onSend(picked);
  }, [picked, sending, onSend]);

  if (!open) return null;

  return (
    <>
      <div className="wai-catalog__backdrop" onClick={onClose} aria-hidden="true" />
      <div className="wai-catalog" role="dialog" aria-modal="true" aria-label="Send product">
        <header className="wai-catalog__head">
          <Package size={16} />
          <span className="wai-catalog__title">Send products</span>
          <button
            type="button"
            className="wai-catalog__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        <label className="wai-catalog__search">
          <Search size={14} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products"
            autoFocus
          />
        </label>

        <div className="wai-catalog__list">
          {loading ? (
            <div className="wai-catalog__empty">Loading products…</div>
          ) : products.length === 0 ? (
            <div className="wai-catalog__empty">
              {provisioned === false
                ? 'Catalog not provisioned yet. Go to /whatsapp/setup → Provision catalog.'
                : search
                  ? 'No products match your search.'
                  : 'No active products to share.'}
            </div>
          ) : (
            products.map((p) => {
              const isPicked = picked.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`wai-catalog__item${isPicked ? ' is-picked' : ''}`}
                  onClick={() => togglePick(p.id)}
                >
                  <div className="wai-catalog__thumb">
                    {p.primaryImage ? (
                      <img src={p.primaryImage} alt="" loading="lazy" />
                    ) : (
                      <Package size={20} />
                    )}
                  </div>
                  <div className="wai-catalog__info">
                    <span className="wai-catalog__name">{p.name}</span>
                    <span className="wai-catalog__price">
                      ₦{Number(p.price).toLocaleString('en-NG')}
                    </span>
                  </div>
                  <span className="wai-catalog__pick" aria-hidden="true">
                    {isPicked ? <Check size={14} /> : null}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <footer className="wai-catalog__foot">
          <button
            type="button"
            className="wai-catalog__send"
            onClick={handleSend}
            disabled={picked.length === 0 || sending}
          >
            {sending
              ? <><RefreshCw size={14} className="wai-spin" /> Sending…</>
              : <><Send size={14} /> Send {picked.length > 0 ? `${picked.length} product${picked.length === 1 ? '' : 's'}` : 'products'}</>
            }
          </button>
        </footer>
      </div>
    </>
  );
}

function Composer({ onSend, onShareCatalog, disabled, sending, sharingCatalog, toast }) {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const textRef = useRef(null);
  const fileRef = useRef(null);

  const handleCatalogSend = useCallback(async (productIds) => {
    const ok = await onShareCatalog(productIds);
    if (ok) setPickerOpen(false);
  }, [onShareCatalog]);

  const reset = useCallback(() => {
    setText('');
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const submit = useCallback((e) => {
    e?.preventDefault?.();
    const value = text.trim();
    if ((!value && !file) || sending) return;
    onSend({ text: value, file });
    reset();
    textRef.current?.focus();
  }, [text, file, sending, onSend, reset]);

  const pickFile = useCallback(() => {
    if (disabled || sending) return;
    fileRef.current?.click();
  }, [disabled, sending]);

  const onFileChange = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) {
      toast.error(`File is too large (${formatBytes(f.size)}). Meta caps uploads at 16 MB.`);
      e.target.value = '';
      return;
    }
    setFile(f);
    textRef.current?.focus();
  }, [toast]);

  const removeFile = useCallback(() => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  return (
    <form className="wai-composer" onSubmit={submit}>
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT_MIMES}
        onChange={onFileChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
      <div className="wai-composer__row">
        {file && (
          <div className="wai-composer__chip" role="status">
            <Paperclip size={13} />
            <span className="wai-composer__chip-name" title={file.name}>{file.name}</span>
            <span className="wai-composer__chip-size">{formatBytes(file.size)}</span>
            <button
              type="button"
              className="wai-composer__chip-x"
              onClick={removeFile}
              aria-label="Remove attachment"
            >
              <X size={12} />
            </button>
          </div>
        )}
        <div className="wai-composer__inputrow">
          <button
            type="button"
            className="wai-composer__attach"
            onClick={pickFile}
            disabled={disabled || sending}
            aria-label="Attach file"
            title="Attach file"
          >
            <Paperclip size={16} />
          </button>
          <button
            type="button"
            className="wai-composer__attach"
            onClick={() => !disabled && !sending && setPickerOpen(true)}
            disabled={disabled || sending}
            aria-label="Send product"
            title="Send product"
          >
            <Package size={16} />
          </button>
          <input
            ref={textRef}
            type="text"
            className="wai-composer__input"
            placeholder={
              disabled ? 'Connect WhatsApp first'
              : file ? 'Add a caption (optional)'
              : 'Type a message'
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={disabled || sending}
            aria-label="Message text"
          />
          <button
            type="submit"
            className="wai-composer__send"
            disabled={disabled || sending || (!text.trim() && !file)}
            aria-label="Send message"
          >
            {sending ? <RefreshCw size={14} className="wai-spin" /> : <Send size={14} />}
          </button>
        </div>
      </div>

      <CatalogPickerSheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSend={handleCatalogSend}
        sending={sharingCatalog}
        toast={toast}
      />
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */

export default function WhatsAppInbox() {
  const toast = useToast();

  const [conversations, setConversations] = useState([]);
  const [convosLoading, setConvosLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sharingCatalog, setSharingCatalog] = useState(false);
  const [mobilePane, setMobilePane] = useState('list'); // list | thread
  const [connection, setConnection] = useState(null);
  const [connLoading, setConnLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const connected = !!connection?.connected;
  const selectedConv = useMemo(
    () => conversations.find((c) => c.id === selectedId) || null,
    [conversations, selectedId],
  );

  /* ── Loaders ─────────────────────────────────────────────────── */

  const loadConnection = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/whatsapp/v2/meta/embedded-signup/connection');
      setConnection(r.data);
    } catch {
      setConnection({ ok: false, connected: false });
    } finally {
      setConnLoading(false);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      const r = await axiosInstance.get(`/whatsapp/v2/conversations?${params.toString()}`);
      if (r.data?.success) {
        setConversations(r.data.conversations || []);
      }
    } catch (err) {
      console.warn('[WhatsAppInbox] loadConversations failed:', err?.message);
    } finally {
      setConvosLoading(false);
    }
  }, [search]);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      setMessagesLoading(true);
      const r = await axiosInstance.get(`/whatsapp/v2/conversations/${convId}/messages`);
      if (r.data?.success) {
        setMessages(r.data.messages || []);
      }
    } catch (err) {
      console.warn('[WhatsAppInbox] loadMessages failed:', err?.message);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const markRead = useCallback(async (convId) => {
    try {
      await axiosInstance.put(`/whatsapp/v2/conversations/${convId}/read`);
      setConversations((cs) =>
        cs.map((c) => (c.id === convId ? { ...c, unreadCount: 0 } : c)),
      );
    } catch {
      /* silent */
    }
  }, []);

  /* ── Effects ─────────────────────────────────────────────────── */

  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  useEffect(() => {
    if (!connected) return undefined;
    loadConversations();
    const id = setInterval(loadConversations, POLL_CONVERSATIONS_MS);
    return () => clearInterval(id);
  }, [connected, loadConversations]);

  useEffect(() => {
    if (!selectedId) return undefined;
    loadMessages(selectedId);
    const id = setInterval(() => loadMessages(selectedId), POLL_MESSAGES_MS);
    return () => clearInterval(id);
  }, [selectedId, loadMessages]);

  /* ── Real-time via Socket.io ─────────────────────────────────
   * The merchant JWT lives in an httpOnly cookie, so we connect with
   * `withCredentials: true` and the backend socket middleware reads
   * the cookie off the handshake (see backend/socket/index.js
   * extractMerchantTokenFromCookies). When this fails — local dev
   * before the backend update, or any disconnect — polling continues
   * to keep the inbox fresh, just slower.
   *
   * Events we listen for (emitted from backend/socket/index.js):
   *   whatsapp:message — new inbound message; append if it belongs
   *                      to the active conversation, otherwise bump
   *                      the unread badge.
   *   whatsapp:sent    — outbound confirmation; reconcile with our
   *                      optimistic append on send.
   *   whatsapp:status  — outbound status update (delivered/read/failed).
   */
  useEffect(() => {
    if (!connected) return undefined;

    const socket = socketIo(`${getSocketUrl()}/whatsapp`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socket.on('connect_error', (err) => {
      // Polling fallback already covers this; just log and let
      // reconnect attempts run.
      console.warn('[WhatsAppInbox] socket connect_error:', err.message);
    });

    socket.on('whatsapp:message', (incoming) => {
      // Append if it lands in the active thread, otherwise drop a hint
      // by bumping that conversation's unread count.
      if (incoming?.conversationId === selectedId) {
        setMessages((cur) => {
          if (cur.some((m) => m.id === incoming.id)) return cur;
          return [...cur, incoming];
        });
      } else if (incoming?.conversationId) {
        setConversations((cs) =>
          cs.map((c) =>
            c.id === incoming.conversationId
              ? {
                  ...c,
                  unreadCount: (c.unreadCount || 0) + 1,
                  lastMessageAt: incoming.createdAt || new Date().toISOString(),
                  lastMessageText: (incoming.text || `[${incoming.messageType?.toLowerCase()}]`).substring(0, 100),
                  lastMessageDirection: 'inbound',
                }
              : c,
          ),
        );
      }
    });

    socket.on('whatsapp:sent', (sent) => {
      // saveOutgoingMessage on the backend emits this when a send
      // round-trip succeeds. We reconcile against the optimistic
      // append we did in handleSend; if the wamid matches, no-op.
      if (sent?.conversationId === selectedId) {
        setMessages((cur) => {
          if (cur.some((m) => m.id === sent.id || m.externalId === sent.externalId)) return cur;
          return [...cur, sent];
        });
      }
    });

    socket.on('whatsapp:status', ({ messageId, status }) => {
      if (!messageId) return;
      setMessages((cur) =>
        cur.map((m) =>
          (m.externalId === messageId || m.id === messageId)
            ? { ...m, status }
            : m,
        ),
      );
    });

    return () => {
      socket.disconnect();
    };
    // selectedId is in deps so the handler closures see the current value
  }, [connected, selectedId]);

  useEffect(() => {
    // Auto-scroll the active thread to the latest message when it grows.
    if (messages.length && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length]);

  /* ── Handlers ────────────────────────────────────────────────── */

  const handleSelect = useCallback((conv) => {
    setSelectedId(conv.id);
    setMobilePane('thread');
    if (conv.unreadCount > 0) markRead(conv.id);
  }, [markRead]);

  const handleBack = useCallback(() => {
    setMobilePane('list');
  }, []);

  const handleSend = useCallback(async ({ text, file }) => {
    if (!selectedConv) return;
    setSending(true);
    try {
      let data;
      if (file) {
        // Media send: multipart to /send-media. Backend uploads the file
        // to Meta first (uploadMedia in MetaCloudAPISender), then sends
        // the message referring to the returned media id. Caption rides
        // along as the `caption` form field if the merchant typed one.
        const formData = new FormData();
        formData.append('to', selectedConv.customerPhone);
        formData.append('file', file);
        if (text) formData.append('caption', text);
        const r = await axiosInstance.post('/whatsapp/v2/messages/send-media', formData);
        data = r.data;
      } else {
        const r = await axiosInstance.post('/whatsapp/v2/messages/send', {
          to: selectedConv.customerPhone,
          text,
        });
        data = r.data;
      }
      if (data?.success && data.message) {
        // Optimistic append so the bubble appears even before the next poll.
        setMessages((cur) => {
          if (cur.some((m) => m.id === data.message.id)) return cur;
          return [...cur, data.message];
        });
        // Optimistically flip the conversation to MERCHANT_HANDLING so
        // the "AI paused" badge shows immediately — the backend also
        // flips it in saveOutgoingMessage, we're just avoiding the 5s
        // poll delay before the merchant sees the state change.
        setConversations((cur) => cur.map((c) =>
          c.id === selectedConv.id && c.handlingMode !== 'MERCHANT_HANDLING'
            ? { ...c, handlingMode: 'MERCHANT_HANDLING', takenOverAt: new Date().toISOString() }
            : c
        ));
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Send failed';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  }, [selectedConv, toast]);

  const [resumingAI, setResumingAI] = useState(false);
  const handleResumeAI = useCallback(async () => {
    if (!selectedConv || resumingAI) return;
    setResumingAI(true);
    try {
      await axiosInstance.post(`/whatsapp/v2/conversations/${selectedConv.id}/resume-ai`);
      setConversations((cur) => cur.map((c) =>
        c.id === selectedConv.id
          ? { ...c, handlingMode: 'AI_HANDLING', takenOverAt: null, takenOverBy: null }
          : c
      ));
      toast.success('AI resumed for this conversation');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not resume AI');
    } finally {
      setResumingAI(false);
    }
  }, [selectedConv, resumingAI, toast]);

  const handleShareCatalog = useCallback(async (productIds) => {
    if (!selectedConv || sharingCatalog) return false;
    setSharingCatalog(true);
    try {
      const r = await axiosInstance.post('/whatsapp/v2/messages/share-catalog', {
        conversationId: selectedConv.id,
        productIds,
      });
      if (r.data?.ok && r.data.message) {
        setMessages((cur) => {
          if (cur.some((m) => m.id === r.data.message.id)) return cur;
          return [...cur, r.data.message];
        });
        toast.success(
          `Sent ${productIds.length} product${productIds.length === 1 ? '' : 's'} to ${selectedConv.customerName || selectedConv.customerPhone}.`
        );
        return true;
      }
      toast.error(r.data?.error || 'Send failed.');
      return false;
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.error?.message || data?.error || err.message || 'Send failed.';
      toast.error(`Send failed: ${msg}`);
      return false;
    } finally {
      setSharingCatalog(false);
    }
  }, [selectedConv, sharingCatalog, toast]);

  /* ── Render ──────────────────────────────────────────────────── */

  const filteredConvos = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.toLowerCase();
    return conversations.filter((c) => {
      const name = (c.customerName || '').toLowerCase();
      const phone = (c.customerPhone || '');
      return name.includes(q) || phone.includes(q);
    });
  }, [conversations, search]);

  return (
    <Shell>
      <FeatureGate
        featureId="marketing.whatsapp"
        upgradeMessage="WhatsApp inbox is part of the Marketing tier. Upgrade to PRO or PREMIUM to use it."
      >
        <div className={`wai-page${mobilePane === 'thread' ? ' is-thread' : ''}`}>
          {/* Sidebar */}
          <aside className="wai-side" aria-label="Conversations">
            <header className="wai-side__head">
              <h1 className="wai-side__title">Inbox</h1>
              <label className="wai-side__search">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Search name or phone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
            </header>
            <div className="wai-side__list">
              {convosLoading ? (
                <ConversationListSkeleton />
              ) : filteredConvos.length === 0 ? (
                <ConversationsEmpty search={search} />
              ) : (
                filteredConvos.map((c) => (
                  <ConversationListItem
                    key={c.id}
                    conv={c}
                    active={c.id === selectedId}
                    onClick={() => handleSelect(c)}
                  />
                ))
              )}
            </div>
          </aside>

          {/* Thread */}
          <section className="wai-thread" aria-label="Messages">
            {connLoading ? null : !connected ? (
              <NotConnectedNotice />
            ) : !selectedConv ? (
              <ThreadEmpty />
            ) : (
              <>
                <ThreadHeader
                  conv={selectedConv}
                  onBack={handleBack}
                  onResumeAI={handleResumeAI}
                  resuming={resumingAI}
                />
                <MessageListView
                  messages={messages}
                  loading={messagesLoading}
                  endRef={messagesEndRef}
                />
                <Composer
                  onSend={handleSend}
                  onShareCatalog={handleShareCatalog}
                  disabled={!connected}
                  sending={sending}
                  sharingCatalog={sharingCatalog}
                  toast={toast}
                />
              </>
            )}
          </section>
        </div>
      </FeatureGate>
    </Shell>
  );
}
